import { DownloadSimpleIcon, XIcon } from "@phosphor-icons/react";
import { Button } from "@reactive-resume/ui";
import { useEffect, useState } from "react";

/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  BANNIÈRE D'INSTALLATION PWA (beforeinstallprompt)
 * ─────────────────────────────────────────────────────────────────────────────
 *  S'affiche discrètement quand le navigateur signale l'éligibilité à
 *  l'installation (Chrome/Edge). Cliquable → déclenche l'invite native.
 *  L'utilisateur peut la fermer (option non intrusive).
 * ─────────────────────────────────────────────────────────────────────────────
 */
type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
};

export const InstallPwaBanner = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const handler = (event: Event) => {
      event.preventDefault();
      const promptEvent = event as BeforeInstallPromptEvent;
      setDeferredPrompt(promptEvent);
      setVisible(true);
    };

    window.addEventListener("beforeinstallprompt", handler as EventListener);
    return () => window.removeEventListener("beforeinstallprompt", handler as EventListener);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") {
      setVisible(false);
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setVisible(false);
    setDismissed(true);
  };

  if (!visible || dismissed) return null;

  return (
    <div className="fixed inset-x-0 bottom-4 z-50 mx-auto flex w-[min(94vw,440px)] items-center gap-3 rounded-2xl border border-border-subtle bg-surface/95 p-3 shadow-2xl backdrop-blur-xl">
      <div className="bg-primary/10 text-primary grid size-11 shrink-0 place-items-center rounded-xl">
        <DownloadSimpleIcon size={20} weight="duotone" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-bold text-text">Installer l'application Mon CV Pro</p>
        <p className="truncate text-xs text-text-muted">
          Accès rapide et notifications même hors-ligne.
        </p>
      </div>
      <Button size="sm" className="shrink-0 rounded-xl" onClick={handleInstall}>
        Installer
      </Button>
      <button
        type="button"
        aria-label="Fermer"
        onClick={handleDismiss}
        className="grid size-7 shrink-0 place-items-center rounded-md text-text-muted hover:bg-canvas-subtle hover:text-text"
      >
        <XIcon size={14} />
      </button>
    </div>
  );
};
